<template>
  <div class="adobot-wrap mx-auto">
    <svg
      class="align-self-center d-block adobot-vect"
      :class="[ 'position-' + position, { 'active': active } ]"
      :style="{ bottom: bottom + 'px' }"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 76 75"
      @touchstart.stop="touchstart"
      @touchmove.stop.prevent
      @touchend.stop="touchend"
      @mousedown.stop.prevent="touchstart"
      @mouseup.stop.prevent="touchend"
    >
      <defs>
        <linearGradient :id="uuid + 'adobot-color'" x1="146.965%" x2="-14.841%" y1="-58.436%" y2="137.067%">
          <stop offset="0%" :stop-color="mainColor"/>
          <stop offset="100%" :stop-color="accent"/>
        </linearGradient>
      </defs>

      <circle cx="38" cy="38" :fill="fill" opacity=".05" r="38" ref="bg"/>

      <circle
        cx="38"
        cy="38"
        :stroke="stroke"
        stroke-width="2"
        stroke-opacity=".2"
        :r="outR"
        transform="rotate(-90 38 38)"
        ref="outerBg"
      />

      <circle
        cx="38"
        cy="38"
        :stroke="stroke"
        stroke-width="2"
        stroke-opacity=".98"
        stroke-dasharray="160"
        :stroke-dashoffset="dashOffset"
        :r="outR"
        transform="rotate(-90 38 38)"
        ref="outerFill"
      />

      <circle
        cx="38"
        cy="38"
        :stroke="stroke"
        stroke-opacity=".2"
        :r="midR"
        transform="rotate(-135 38.5 38)"
        ref="midCirc"
      />

      <circle
        cx="38.5"
        cy="38.5"
        r="6.5"
        :stroke="stroke"
        :stroke-width="innerW"
        transform="rotate(-135 38.5 38.5)"
        ref="inner"
      />

      <text
        class="adobe-clean-xbold demo-text"
        :class="[ demoClass ]"
        x="38"
        y="74"
        font-size="0.75rem"
        text-anchor="middle"
        :letter-spacing="demoSpace + 'em'"
        v-if="willDemo"
      >
        <tspan style="opacity: 0" v-for="letter in demoTxt.split('')" v-html="letter" ref="demoChars"></tspan>
      </text>

    </svg>
  </div>
</template>

<script>
  import { shuffleArr } from '@/lib/funcs'
  import { TimelineMax, Power2 } from 'gsap'
  import _ from 'lodash'
  import StateMixin from '@/components/report/mixins/StateMixin'

  export default {
    name: 'Adobot',
    mixins: [ StateMixin ],
    props: {
      bg: { type: String, required: true },
      active: { type: Boolean, default () { return false } },
      willDemo: { type: Boolean, default () { return false } },
      demoActive: { type: Boolean, default () { return false } }
    },
    data () {
      return {
        on: false,
        hasActivated: false,
        hasHeld: false,
        position: 'fixed',
        bottom: 25,
        wp: null,
        interval: null,
        transTime: 0.5,
        holdTime: 1,
        innerW: 0.1,
        tl: new TimelineMax(),
        dashOffset: 160,  // -> 0
        outR: 20, // -> 25
        midR: 12, // -> 16
        demoTxt: 'PRESS AND HOLD',
        demoSpace: 0.7, // -> 0.125,
        demoClass: '' // -> 'd-none'
      }
    },
    computed: {
      whiteBg () { return this.bg !== this.mainColor && this.bg !== this.accent },

      stroke () { return this.whiteBg ? 'url(#' + this.uuid + 'adobot-color)' : '#fff' },

      fill () { return this.whiteBg ? '#a0a0a0' : '#fff' },

      transStagger () { return this.transTime * 0.15 },

      holdStagger () { return this.holdTime * 0.15 }
    },
    watch: {
      active ( val ) {
        switch ( val ) {
          case true:
            this.hasActivated ? this.tl.tweenTo( 'introduced' ) : this.tweenIn()
            break
          case false:
            this.tweenOut()
            break
        }
      },
      demoActive ( val ) {
        if ( val ) {
          this.touchstart()
          this.$once( 'holdComplete', () => setTimeout( () => {
            this.$once( 'introduced', () => {
              this.demoClass = 'd-none'
            } )
            this.touchend()
          }, 1250 ) )
        }
      },
      hasActivated ( val ) {
        if ( val ) {
          this.interval = window.setInterval( () => this.activeGuard(), 1000 )
        }
      }
    },
    methods: {
      touchstart: _.throttle( function () {
        this.$emit( 'on' )
        this.hasHeld ? this.tl.tweenTo( 'holdComplete' ) : this.tweenHold()
      }, 150 ),

      touchend: _.throttle( function () {
        this.$emit( 'off' )
        this.tl.tweenTo( 'introduced' )
      }, 150 ),

      setTweens () {
        this.tl.set( this.$refs.bg, { transformOrigin: '50% 50%', scale: 0 } )
          .set( [ this.$refs.inner, this.$refs.midCirc, this.$refs.outerFill, this.$refs.outerBg ], { opacity: 0 } )
      },

      tweenIn () {
        this.tl.to( this.$refs.bg, this.transTime, { scale: 1, ease: Power2.easeInOut }, 'in' )
          .to( this, this.transTime, { innerW: 2, ease: Power2.easeInOut }, 'in' )
          .to( [ this.$refs.inner, this.$refs.midCirc, this.$refs.outerFill, this.$refs.outerBg ], this.transTime / 2, { opacity: 1 }, 'in+=' + (this.transTime / 4) )
          .to( this, this.transTime, { midR: 16, ease: Power2.easeInOut }, 'in' )
          .to( this, this.transTime, {
            outR: 25,
            ease: Power2.easeInOut
          }, 'in+=' + this.transStagger )
          .addLabel( 'introduced' )
          .call( () => this.$emit( 'introduced' ) )

        this.hasActivated = true
      },

      tweenOut () { this.tl.tweenTo( 'in' ) },

      tweenHold () {
        this.hasHeld = true
        if ( this.willDemo ) {
          let chars = shuffleArr( [ ...this.$refs.demoChars ] )
          this.tl.to( this, this.holdTime * 0.65, { demoSpace: 0.125, ease: Power2.easeOut }, 'hold' )
            .staggerTo( chars, this.holdTime / 2, { opacity: 1 }, this.holdStagger / 4, 'hold' )
        }
        this.tl.to( this, this.holdTime, { innerW: 5, dashOffset: 0, ease: Power2.easeInOut }, 'hold' )
          .to( this, this.holdTime / 2, { midR: 12, ease: Power2.easeInOut }, 'hold' )
          .to( this, this.holdTime / 2, { outR: 20, ease: Power2.easeInOut }, 'hold+=' + this.holdStagger )
          .to( this.$refs.bg, this.holdTime / 2, { scale: 1.25, ease: Power2.easeInOut }, 'hold+=' + this.holdStagger )
          .addLabel( 'holdComplete' )
          .call( () => this.$emit( 'holdComplete' ) )
      },

      activeGuard () {
        if ( this.tl.progress() > 0 && this.$el.getBoundingClientRect().top > window.innerHeight * 1.5 ) {
          this.tweenOut()
        }
      }
    },

    mounted () {
      this.setTweens()
      this.wp = new window.Waypoint( {
        element: this.$el,
        handler: dir => {
          this.position = dir === 'down' ? 'static' : 'fixed'
        },
        offset: () => window.innerHeight - this.bottom - this.$el.clientHeight
      } )

      if ( this.active ) {
        this.hasActivated ? this.tl.tweenTo( 'introduced' ) : this.tweenIn()
      }
    },

    beforeDestroy () {
      if ( this.interval ) window.clearInterval( this.interval )
    }
  }
</script>

<style lang="scss">
  @import "../../assets/scss/global/the_goods";

  .adobot-wrap {
    margin-top : rem(25);
    width      : rem(75);
    height     : rem(75);

    @include screen-md-lg-xl {
      margin-top : rem(20);
    }

    .demo-text {
      fill : $white;
    }
  }

  .adobot-vect {
    width          : rem(75);
    height         : rem(75);
    z-index        : 15;
    pointer-events : none;
    transform      : translateY(100%);
    transition     : transform 0.5s ease-in-out;

    &.active {
      transform      : translateY(0);
      pointer-events : auto;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/report/Adobot.vue